import { useState, useEffect, useCallback } from "react";
import { Users, Bot, Plus, Trash2, Power, ShieldCheck, Save } from "lucide-react";
import { toast } from "sonner";
import { ROLE_LABELS } from "@/lib/roles";

const HUMAN_OPTIONS = [
  { value: "", label: "Normal user" },
  { value: "content_creator", label: "Content Creator" },
  { value: "moderator", label: "Moderator" },
  { value: "superadmin", label: "Superadmin" },
];
const BOT_OPTIONS = [
  { value: "content_creator", label: "Content Creator" },
  { value: "moderator", label: "Moderator" },
];

function MemberRow({ m, onSave, isBot }) {
  const [role, setRole] = useState(m.roles?.[0] || "");
  const options = isBot ? BOT_OPTIONS : HUMAN_OPTIONS;
  const dirty = (m.roles?.[0] || "") !== role;
  return (
    <div className="flex items-center gap-3 py-2.5 border-b border-gray-50 last:border-0" data-testid={`member-${m.user_id}`}>
      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-view-a to-view-b flex items-center justify-center text-white text-xs font-bold shrink-0">
        {isBot ? <Bot className="w-4 h-4" /> : (m.name || "?").charAt(0).toUpperCase()}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-dark truncate flex items-center gap-1.5">
          {m.name || "Unnamed"}
          {m.disabled && <span className="text-[9px] font-bold text-amber-700 bg-amber-50 px-1.5 py-0.5 rounded">DISABLED</span>}
        </p>
        <p className="text-[11px] text-neutral truncate">{isBot ? "bot account" : m.email}</p>
      </div>
      <select
        value={role}
        onChange={(e) => setRole(e.target.value)}
        className="text-xs border border-gray-200 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-view-a/30"
        data-testid={`role-select-${m.user_id}`}
      >
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
      <button
        onClick={() => onSave(m.user_id, role ? [role] : [])}
        disabled={!dirty}
        className="p-1.5 text-view-a hover:bg-view-a-light rounded-lg transition-colors disabled:opacity-30"
        title="Save role"
        data-testid={`role-save-${m.user_id}`}
      >
        <Save className="w-4 h-4" />
      </button>
    </div>
  );
}

export default function TeamPanel({ api }) {
  const [team, setTeam] = useState({ members: [], bots: [] });
  const [loading, setLoading] = useState(true);
  const [showBotForm, setShowBotForm] = useState(false);
  const [botName, setBotName] = useState("");
  const [botRole, setBotRole] = useState("content_creator");

  const fetchTeam = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/team");
      setTeam(data);
    } catch {
      toast.error("Failed to load team");
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { fetchTeam(); }, [fetchTeam]);

  const saveRoles = async (userId, roles) => {
    try {
      await api.put(`/admin/users/${userId}/roles`, { roles });
      toast.success("Role updated");
      fetchTeam();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update role");
    }
  };

  const createBot = async (e) => {
    e.preventDefault();
    if (!botName.trim()) return;
    try {
      await api.post("/admin/bots", { name: botName.trim(), role: botRole });
      toast.success("Bot created");
      setBotName(""); setShowBotForm(false);
      fetchTeam();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create bot");
    }
  };

  const toggleBot = async (userId) => {
    try { await api.put(`/admin/bots/${userId}/toggle`); fetchTeam(); }
    catch { toast.error("Failed"); }
  };

  const deleteBot = async (userId) => {
    try { await api.delete(`/admin/bots/${userId}`); toast.success("Bot deleted"); fetchTeam(); }
    catch (err) { toast.error(err.response?.data?.detail || "Failed to delete"); }
  };

  return (
    <div className="bg-white rounded-2xl shadow-card p-6 mb-6" data-testid="team-panel">
      <h2 className="font-heading text-xl text-dark flex items-center gap-2 mb-1">
        <ShieldCheck className="w-5 h-5 text-view-a" /> TEAM & BOTS
      </h2>
      <p className="text-xs text-neutral mb-4">Superadmin only. Assign roles to people and automated bots. Bots can never be Superadmin.</p>

      {loading ? (
        <div className="h-24 bg-gray-50 rounded-xl animate-pulse" />
      ) : (
        <>
          {/* Bots */}
          <div className="mb-5">
            <div className="flex items-center justify-between mb-1.5">
              <h3 className="text-xs font-bold tracking-wider uppercase text-neutral flex items-center gap-1.5"><Bot className="w-3.5 h-3.5" /> Bots ({team.bots.length})</h3>
              <button onClick={() => setShowBotForm((s) => !s)} className="flex items-center gap-1 text-xs font-semibold text-view-a" data-testid="new-bot-toggle">
                <Plus className="w-3.5 h-3.5" /> New bot
              </button>
            </div>
            {showBotForm && (
              <form onSubmit={createBot} className="flex gap-2 mb-2 p-3 bg-gray-50 rounded-xl" data-testid="new-bot-form">
                <input
                  value={botName}
                  onChange={(e) => setBotName(e.target.value)}
                  placeholder="Bot name (e.g. GrokBot)"
                  className="flex-1 text-sm px-3 py-1.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-view-a/30"
                  data-testid="new-bot-name"
                />
                <select value={botRole} onChange={(e) => setBotRole(e.target.value)} className="text-xs border border-gray-200 rounded-lg px-2" data-testid="new-bot-role">
                  {BOT_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
                <button type="submit" className="px-3 py-1.5 text-xs font-semibold text-white bg-view-a rounded-lg" data-testid="new-bot-submit">Create</button>
              </form>
            )}
            <div className="rounded-xl border border-gray-100 px-3">
              {team.bots.map((b) => (
                <div key={b.user_id} className="flex items-center gap-2" data-testid={`bot-${b.user_id}`}>
                  <div className="flex-1"><MemberRow m={b} onSave={saveRoles} isBot /></div>
                  <button onClick={() => toggleBot(b.user_id)} className="p-1.5 text-neutral hover:text-amber-600 rounded-lg" title={b.disabled ? "Enable" : "Disable"} data-testid={`bot-toggle-${b.user_id}`}>
                    <Power className="w-4 h-4" />
                  </button>
                  <button onClick={() => deleteBot(b.user_id)} className="p-1.5 text-neutral hover:text-red-600 rounded-lg" title="Delete" data-testid={`bot-delete-${b.user_id}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Members */}
          <h3 className="text-xs font-bold tracking-wider uppercase text-neutral flex items-center gap-1.5 mb-1.5"><Users className="w-3.5 h-3.5" /> People ({team.members.length})</h3>
          <div className="rounded-xl border border-gray-100 px-3 max-h-80 overflow-y-auto" data-testid="members-list">
            {team.members.map((m) => <MemberRow key={m.user_id} m={m} onSave={saveRoles} />)}
          </div>
        </>
      )}
    </div>
  );
}
